import { forwardRef, useImperativeHandle, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Plus, Minus, Layers } from "lucide-react";

const BOARDS = [1, 2, 3, 4, 5, 6, 7];
const keyOf = (n) => `board_${n}`;

const BOARD_SOURCES = {
  1: "Checkmate",
  2: "Checkmate",
  3: "Checkmate",
  4: "Orbituary",
  5: "Ruin",
  6: "Ruin",
  7: "Orbituary",
};

function getBoardCardColor(boardNum) {
  // Map board numbers to their card colors
  const colorMap = {
    1: 'text-red-500',      // Board 1 - Red card
    2: 'text-blue-400',     // Board 2 - Blue card
    3: 'text-orange-500',   // Board 3 - Board card
    4: 'text-blue-500',     // Board 4 - Wasteland card
    5: 'text-green-500',    // Board 5 - Crypt card
    6: 'text-yellow-500',   // Board 6 - Last Resort card
    7: 'text-orange-500',   // Board 7 - default orange
  };
  return colorMap[boardNum] || 'text-orange-500';
}

function getGlowColor(colorClass, mode) {
  if (mode === 'private') {
    return 'rgba(16, 185, 129, 1.0)'; // emerald for all numbers in private mode
  }
  const colorMap = {
    'text-red-500':    'rgba(239, 68, 68, 1.0)',
    'text-blue-400':   'rgba(96, 165, 250, 1.0)',
    'text-orange-500': 'rgba(249, 115, 22, 1.0)',
    'text-blue-500':   'rgba(59, 130, 246, 1.0)',
    'text-green-500':  'rgba(34, 197, 94, 1.0)',
    'text-yellow-500': 'rgba(234, 179, 8, 1.0)',
    'text-emerald-500': 'rgba(16, 185, 129, 1.0)',
  };
  return colorMap[colorClass] || 'rgba(249, 115, 22, 1.0)';
}

const BoardTracker = forwardRef(function BoardTracker({ mode = "group" }, ref) {
  const [counts, setCounts] = useState(() =>
    BOARDS.reduce((a, n) => ({ ...a, [keyOf(n)]: 0 }), {})
  );
  const [busy, setBusy] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/board-counts");
      setCounts(data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    refresh();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const adjust = useCallback(async (boardKey, delta) => {
    setBusy(boardKey);
    try {
      const { data } = await api.post(`/board-counts/${boardKey}/adjust`, { delta });
      setCounts(data);
    } catch (e) {
    } finally {
      setBusy(null);
    }
  }, []);

  const decrementAll = useCallback(async () => {
    await Promise.all(
      BOARDS.map(async (n) => {
        const k = keyOf(n);
        try {
          const { data } = await api.post(`/board-counts/${k}/adjust`, { delta: -1 });
          setCounts((prev) => ({ ...prev, ...data }));
        } catch (e) {}
      })
    );
    // Re-fetch authoritative state after all adjustments
    await refresh();
  }, [refresh]);

  // Expose imperative methods to parent (Dashboard) so Claim from TimerCard
  // can drive the count without prop-drilling state up.
  useImperativeHandle(ref, () => ({
    increment: (boardKey) => adjust(boardKey, 1),
    decrementAll,
    refresh,
    getCounts: () => counts,
  }), [adjust, counts, decrementAll, refresh]);

  const total = BOARDS.reduce((s, n) => s + (counts[keyOf(n)] || 0), 0);
  const collected = BOARDS.filter((n) => (counts[keyOf(n)] || 0) > 0).length;

  return (
    <div
      className="border border-orange-500/20 bg-[#121822]/70 px-4 py-3 relative corner-tl corner-tr corner-bl corner-br"
      data-testid="board-tracker"
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-[11px] tracking-[0.3em] uppercase text-slate-500">
            Board Tracker
          </span>
        </div>
        <div className="text-xs text-orange-400 tabular-nums" data-testid="board-summary">
          {collected} / 7 unique · {total} total
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        {BOARDS.map((n) => {
          const k = keyOf(n);
          const v = counts[k] || 0;
          const has = v > 0;
          return (
            <div
              key={n}
              data-testid={`board-cell-${n}`}
              className={`relative flex flex-col items-center justify-between border ${
                has ? "border-emerald-500/50 bg-emerald-500/5" : "border-slate-800 bg-[#0B0E14]/60"
              } px-2 py-2 transition-colors gap-2`}
            >
              <div className="flex flex-col items-center leading-none w-full">
                <span className="text-[10px] tracking-[0.2em] uppercase text-slate-400">
                  {BOARD_SOURCES[n]}
                </span>
                <span
                  className={`font-dseg7 text-2xl leading-none mt-1 tabular-nums text-slate-200`}
                  style={{ textShadow: `0 0 5px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}, 0 0 10px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}, 0 0 20px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}, 0 0 30px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}` }}
                >
                  {n}
                </span>
              </div>
              <div className="flex items-center gap-0.5 w-full justify-center">
                <button
                  onClick={() => adjust(k, -1)}
                  disabled={busy === k || v <= 0}
                  data-testid={`board-${n}-dec`}
                  aria-label={`Decrease Board ${n}`}
                  className="w-5 h-5 border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors flex items-center justify-center disabled:opacity-30 disabled:hover:border-slate-700 disabled:hover:text-slate-400"
                >
                  <Minus className="w-2.5 h-2.5" />
                </button>
                <div
                  className={`font-dseg7 tabular-nums w-8 text-center text-base font-bold text-slate-200`}
                  style={{ textShadow: `0 0 5px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}, 0 0 10px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}, 0 0 20px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}, 0 0 30px ${getGlowColor(v > 0 ? 'text-emerald-500' : 'text-orange-500', mode)}` }}
                  data-testid={`board-${n}-count`}
                >
                  {v}
                </div>
                <button
                  onClick={() => adjust(k, 1)}
                  disabled={busy === k}
                  data-testid={`board-${n}-inc`}
                  aria-label={`Increase Board ${n}`}
                  className="w-5 h-5 border border-slate-700 text-slate-400 hover:border-orange-500 hover:text-orange-400 transition-colors flex items-center justify-center disabled:opacity-30"
                >
                  <Plus className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Helper used by TimerCard / Dashboard to map a card name → board key.
export function boardKeyFromName(name) {
  const m = /^Board\s+([1-7])$/i.exec((name || "").trim());
  return m ? `board_${m[1]}` : null;
}

export default BoardTracker;
